// Shared print / PDF stylesheet for the four public client-facing document
// pages: presentation (/p/:uuid), estimate + Quote (/o/:uuid),
// invoice (/i/:uuid) and PO (/po/:uuid).
//
// Seeded from PublicPresentationPage's original PRINT_STYLES block
// (2026-05-20) and hardened for color output.
//
// CEO 2026-07-24: "When downloading as a PDF (either a presentation,
// estimate, or SO) — make sure it's color, and a clean interface."
// The decisive rule is `print-color-adjust: exact` (and its -webkit-
// prefix): it tells the browser to render backgrounds + brand accents in
// print instead of dropping them to grayscale/white, which is the classic
// cause of a washed-out PDF.
//
// Chrome that is NOT part of the document (sticky nav, Download button,
// approve / pay bars, preview banners) is excluded from print via the
// shared `print:hidden` class that every page already puts on those
// elements. We deliberately do NOT blanket-hide <header>/<footer> here:
// the document pages use an inner <header> for the title block that MUST
// survive printing — the sticky top bar and the footer already carry
// `print:hidden` themselves.

import type { ReactNode } from "react";

export const DOCUMENT_PRINT_STYLES = `
  @media print {
    @page { margin: 0.5in; }
    html, body {
      background: #ffffff !important;
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    main, main * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
    }
    /* Hide interactive chrome flagged by the shared print:hidden class
       (sticky bars, Download button, approve / pay bars, preview banners). */
    .print\\:hidden { display: none !important; }
    .print\\:pt-0 { padding-top: 0 !important; }
    .print\\:pb-0 { padding-bottom: 0 !important; }
    /* Page-break hygiene — never split a line-item card, table row, or list
       item across a page boundary. */
    li, tr { break-inside: avoid; }
    .rounded-3xl, .rounded-2xl, .rounded-xl { break-inside: avoid; }
    /* Full-bleed the document body for print. */
    main {
      padding: 0 !important;
      max-width: 100% !important;
    }
  }
`;

/**
 * Renders the shared document print stylesheet. Drop this near the top of a
 * public document page's root element (the way PublicPresentationPage did
 * with its local <style> block) so browser print-to-PDF keeps brand color.
 */
export function DocumentPrintStyles() {
  return <style>{DOCUMENT_PRINT_STYLES}</style>;
}

/**
 * Wrap a document's content so its PDF has no browser date / title / web
 * address in the corners and a clean margin on EVERY page (Stephanie
 * 2026-11-03/04: "make sure the quote PDF doesn't have these same issues, and
 * any other PDF form"). The page margin is 0 (so the browser has nowhere to
 * print its corners) and a blank band repeats at the top and bottom of each
 * printed page (a table's header/footer rows repeat per page). On screen it is
 * just a plain block. `sidePad` is the left/right margin on paper.
 */
export function PrintSheet({ children, sidePad = "0.6in" }: { children: ReactNode; sidePad?: string }) {
  return (
    <>
      <style>{`@media print {
        @page { margin: 0; }
        .print-sheet { display: table !important; width: 100%; }
        .print-sheet > thead { display: table-header-group !important; }
        .print-sheet > tfoot { display: table-footer-group !important; }
        .print-sheet > tbody { display: table-row-group !important; }
        .print-sheet > tbody > tr { display: table-row !important; break-inside: auto !important; }
        .print-sheet > tbody > tr > td { display: table-cell !important; padding: 0 ${sidePad}; }
      }`}</style>
      <table className="print-sheet block w-full">
        <thead className="hidden"><tr><td><div style={{ height: "0.5in" }} /></td></tr></thead>
        <tfoot className="hidden"><tr><td><div style={{ height: "0.5in" }} /></td></tr></tfoot>
        <tbody className="block"><tr className="block"><td className="block">{children}</td></tr></tbody>
      </table>
    </>
  );
}
